import { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, FileSpreadsheet, Zap, ArrowRight, Upload as UploadIcon, Database } from "lucide-react";

export default function Onboarding() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);

  const loadSample = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/datasets/load-sample");
      toast.success(`${data.created.length} sample datasets loaded`);
      // Also auto-create dashboards
      for (const d of data.created) {
        try { await api.post(`/datasets/${d.id}/auto-dashboard`); } catch {}
      }
      nav(`/t/${slug}/ask`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 lg:p-12">
      <div className="chip chip-primary mb-6"><Sparkles size={12} /> Getting started</div>
      <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight text-slate-900 mb-4">
        Let's set up your workspace
      </h1>
      <p className="text-lg text-slate-600 max-w-2xl mb-10">
        Pick one option to bring your workforce data into PeopleIQ. You can always add more later.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Sample data */}
        <div className="card relative overflow-hidden" data-testid="option-sample">
          <div className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-10" style={{ background: "radial-gradient(circle, #2563EB, transparent 70%)" }} />
          <div className="chip chip-success mb-4"><Zap size={12} /> Fastest</div>
          <h3 className="font-display text-2xl font-medium text-slate-900 mb-2">Try with sample HR data</h3>
          <p className="text-sm text-slate-500 mb-5 leading-relaxed">
            Load 3 realistic datasets — Employee Master, Attrition, and Compensation — and see PeopleIQ working in seconds.
          </p>
          <div className="space-y-2 mb-6">
            <SampleRow name="Employee Master" rows="220 employees" tag="Headcount · Demographics · Hierarchy" />
            <SampleRow name="Attrition" rows="29 exits" tag="Voluntary · Involuntary · Reasons" />
            <SampleRow name="Compensation" rows="220 records" tag="Base · Bonus · Compa-Ratio" />
          </div>
          <button onClick={loadSample} disabled={busy} data-testid="load-sample" className="btn-primary w-full justify-center">
            {busy ? "Loading…" : <>Load sample data <ArrowRight size={14} /></>}
          </button>
        </div>

        {/* Upload own */}
        <div className="card" data-testid="option-upload">
          <div className="chip mb-4"><UploadIcon size={12} /> Bring your own</div>
          <h3 className="font-display text-2xl font-medium text-slate-900 mb-2">Upload your files</h3>
          <p className="text-sm text-slate-500 mb-5 leading-relaxed">
            Drop CSV or Excel files from any HR system — Workday, SAP, BambooHR, Darwinbox, or a spreadsheet.
            PeopleIQ auto-detects your columns and builds dashboards for you.
          </p>
          <ul className="space-y-2 mb-6 text-sm text-slate-600">
            <Feature text="Multi-file upload from multiple HR systems" />
            <Feature text="Auto column detection & data quality scoring" />
            <Feature text="Auto-generated workforce dashboard" />
            <Feature text="Ask AI questions in plain English" />
          </ul>
          <Link to={`/t/${slug}/upload`} className="btn-ghost w-full justify-center" data-testid="go-upload">
            Upload files <ArrowRight size={14} />
          </Link>
        </div>
      </div>

      <div className="mt-10 flex justify-center">
        <Link to={`/t/${slug}/ask`} className="text-sm text-slate-500 hover:text-slate-900" data-testid="skip">
          Skip for now — I'll add data later →
        </Link>
      </div>
    </div>
  );
}

function SampleRow({ name, rows, tag }) {
  return (
    <div className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">
      <FileSpreadsheet size={14} className="text-[var(--primary)]" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-800">{name}</div>
        <div className="text-xs text-slate-500 truncate">{tag}</div>
      </div>
      <span className="text-xs text-slate-500 mono">{rows}</span>
    </div>
  );
}
function Feature({ text }) {
  return (
    <li className="flex items-start gap-2">
      <div className="w-1 h-1 rounded-full bg-[var(--primary)] mt-2" />
      <span>{text}</span>
    </li>
  );
}
